import { CommonModule } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    Injector
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Store } from '@ngxs/store';
import { TourMatMenu, TourService } from 'ngx-ui-tour-md-menu';
import { filter } from 'rxjs';
import { AppBrandLogoComponent } from './core/ui/brand-logo/app-brand-logo.component';
import { AppNavButtonComponent } from './core/ui/nav-button/app-nav-button.component';
import { AppNavItemComponent } from './core/ui/nav-item/app-nav-item.component';
import { AppPlanBadgeComponent } from './core/ui/plan-badge/app-plan-badge.component';
import { AppTopbarComponent } from './core/ui/topbar/app-topbar.component';
import { Logout } from './features/auth/auth.actions';
import { AuthState } from './features/auth/auth.state';
import { AppTourService } from './features/guided-tour/app-tour.service';
import { SubscriptionsState } from './features/subscriptions/subscriptions.state';

@Component({
    standalone: true,
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [
        CommonModule,
        MatSidenavModule,
        AppBrandLogoComponent,
        AppNavItemComponent,
        AppTopbarComponent,
        MatButtonModule,
        MatCardModule,
        AppNavButtonComponent,
        RouterModule,
        MatIconModule,
        MatListModule,
        AppPlanBadgeComponent,
        TranslateModule,
        ...TourMatMenu
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
    private store = inject(Store);
    private destroyRef = inject(DestroyRef);
    private injector = inject(Injector);
    /** Shared ngx-ui-tour instance for the shell step template. */
    readonly tourService = inject(TourService);
    /** App onboarding (skip / finish / welcome). */
    readonly appTour = inject(AppTourService);

    isLogin$ = this.store.select(AuthState.isLogin);
    isAdmin = this.store.selectSignal(AuthState.isAdmin);

    /** Single source of truth for current subscription (see Task 6). */
    subscription$ = this.store.select(SubscriptionsState.current);

    constructor() {
        this.isLogin$
            .pipe(
                filter((loggedIn) => !!loggedIn),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                afterNextRender(
                    () => {
                        queueMicrotask(() => this.appTour.maybeOfferWelcome());
                    },
                    { injector: this.injector }
                );
            });
    }

    @Dispatch()
    logout = () => new Logout();
}
